import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";

import { useAPIStore } from "../../Store/APIStore";
// Store
import { pathTitles, useStore } from "../../Store/Store";
// Components
import { ArrowRight, Interested, OptimizedImage, SEOHead } from "../Common";

export const EnhancedPuppyHome = () => {
  const getLink = useStore((state) => state.getLink);
  const {
    puppies,
    loading,
    error,
    fetchPuppiesInitial,
    fetchPuppies,
    clearError,
    puppyCounts,
  } = useAPIStore();

  const [amountToLoad, setAmountToLoad] = useState(8);
  const [showButton, setShowButton] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all', 'available', 'sold'

  const getFilteredPuppies = useCallback(() => {
    if (filter === "available") {
      return puppies.filter((puppy) => puppy.available);
    } else if (filter === "sold") {
      return puppies.filter((puppy) => !puppy.available);
    }
    return puppies;
  }, [puppies, filter]);

  useEffect(() => {
    fetchPuppiesInitial(); // Use optimized initial load
  }, []);

  useEffect(() => {
    const filteredPuppies = getFilteredPuppies();
    setShowButton(amountToLoad < filteredPuppies.length);
  }, [puppies, amountToLoad, filter, getFilteredPuppies]);

  const handleLoadMore = () => {
    const filteredPuppies = getFilteredPuppies();
    if (amountToLoad < filteredPuppies.length) {
      setAmountToLoad((state) => state + 8);
    }
    if (amountToLoad + 8 >= filteredPuppies.length) {
      setShowButton(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    // Prevent rapid successive clicks
    if (newFilter === filter) return;

    setFilter(newFilter);
    setAmountToLoad(8);
    setShowButton(true); // Reset show button when filter changes
  };

  if (loading.puppies) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading puppies...</span>
        </Spinner>
        <p className="mt-3">Loading our adorable puppies...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-3">
        <Alert variant="danger" dismissible onClose={clearError}>
          <Alert.Heading>Error Loading Puppies</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchPuppies}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  const filteredPuppies = getFilteredPuppies();

  if (filteredPuppies.length === 0) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="info">
          <h4>No Puppies Available</h4>
          <p>
            {filter === "available"
              ? "We don't have any puppies available right now, but check back soon!"
              : filter === "sold"
                ? "No sold puppies to display."
                : "We're updating our puppy listings. Please check back soon!"}
          </p>
          <Button
            variant="primary"
            as={Link}
            to={getLink(pathTitles.CONTACTUS)}
          >
            Contact Us for Updates
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* SEO Head for puppies listing page */}
      <SEOHead
        pageType="puppies-list"
        canonical={`${window.location.origin}/puppies`}
      />

      <Container className="puppy_home_container small_container clear">
        <Row className="titles">
          <Card.Title className="title">Trained Puppies for Sale</Card.Title>
          <Card.Subtitle className="title two">View Our Puppies</Card.Subtitle>
        </Row>

        {/* Urgency Banner */}
        {puppyCounts.available > 0 && (
          <Row className="mb-3">
            <Col>
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  border: "2px solid #ffc107",
                  borderRadius: "8px",
                  padding: "15px 20px",
                  textAlign: "center",
                  marginTop: "20px",
                }}
              >
                <span
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "600",
                    color: "#856404",
                  }}
                >
                  🔥 Only {puppyCounts.available}{" "}
                  {puppyCounts.available === 1 ? "Puppy" : "Puppies"} Available
                  Now!
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.95rem",
                    color: "#856404",
                    marginTop: "5px",
                  }}
                >
                  These trained puppies are ready for their forever homes.
                  Reserve yours today!
                </span>
              </div>
            </Col>
          </Row>
        )}

        {/* Filter Buttons */}
        <Row className="mb-4">
          <Col className="text-center">
            <div className="btn-group" role="group" aria-label="Puppy filters">
              <Button
                variant={filter === "all" ? "primary" : "outline-primary"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange("all");
                }}
                active={filter === "all"}
              >
                All Puppies ({puppyCounts.total})
              </Button>
              <Button
                variant={filter === "available" ? "success" : "outline-success"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange("available");
                }}
                active={filter === "available"}
              >
                Available ({puppyCounts.available})
              </Button>
              <Button
                variant={filter === "sold" ? "secondary" : "outline-secondary"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange("sold");
                }}
                active={filter === "sold"}
              >
                Sold ({puppyCounts.sold})
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <div className="view_puppies">
            {filteredPuppies.map((puppy, i) => {
              if (i < amountToLoad) {
                return (
                  <Col
                    key={puppy.id || puppy.image || i}
                    sm={{ span: 10 }}
                    md={{ span: 5 }}
                    lg={{ span: 3 }}
                    className="col"
                  >
                    <Card
                      className="card_inner"
                      as={Link}
                      to={`${getLink(pathTitles.VIEWPUPPIES)}${puppy.profilePath}`}
                      style={{
                        position: "relative",
                        overflow: "hidden",
                        minHeight: "200px",
                        borderRadius: "8px",
                        border: "none",
                      }}
                    >
                      {/* Enhanced Image with Optimization */}
                      <OptimizedImage
                        src={
                          puppy.carouselOptimized &&
                          puppy.carouselOptimized[0] &&
                          puppy.carouselOptimized[0].original
                            ? puppy.carouselOptimized[0]
                            : puppy.mainImageUrl ||
                              `/assets/images/puppies/${puppy.image || "placeholder"}.jpg`
                        }
                        alt={`${puppy.cardHeaderTitle || puppy.name} - ${puppy.breed} puppy for sale`}
                        className="puppy_image profile"
                        lazy={true}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          zIndex: 1,
                        }}
                      />

                      {/* Availability Badge */}
                      <Badge
                        bg={puppy.available ? "success" : "secondary"}
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          zIndex: 3,
                          fontSize: "0.85rem",
                          padding: "6px 12px",
                          fontWeight: "600",
                        }}
                      >
                        {puppy.available ? "Available" : "Sold"}
                      </Badge>

                      {/* Price Badge */}
                      {puppy.price && puppy.price !== "unavailable" && (
                        <Badge
                          bg="dark"
                          style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            zIndex: 3,
                            fontSize: "0.9rem",
                            padding: "6px 12px",
                            fontWeight: "600",
                            backgroundColor: "rgba(0,0,0,0.7)",
                          }}
                        >
                          {puppy.price}
                        </Badge>
                      )}
                      <div
                        className="header_div"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          zIndex: 2,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                          color: "white",
                          padding: "15px",
                        }}
                      >
                        <Card.Header
                          className="card_header"
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "white",
                            padding: "0",
                            fontSize: "1.1rem",
                            fontWeight: "bold",
                          }}
                        >
                          {puppy.cardHeaderTitle || puppy.name}
                        </Card.Header>
                        <ArrowRight
                          className="arrow_right"
                          style={{
                            color: "white",
                            position: "absolute",
                            right: "15px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        />
                      </div>
                    </Card>
                  </Col>
                );
              }
              return null;
            })}
          </div>

          {showButton && (
            <div className="text-center mt-4">
              <Button className="almond" onClick={handleLoadMore}>
                View More...
              </Button>
            </div>
          )}
        </Row>
      </Container>

      <Interested />
    </>
  );
};
